import Icon1 from "@assets/icons/Icon";
import { CopiableInfosSection } from "./CopiableInfosSection";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { getLocalFromFlutter } from "@services/timeHelpers";
import { Project } from "types";
import { useState, useEffect } from "react";
import { showNotification } from "context/notification.service";
import { colors } from "sexy";
import { usersApi } from "@services/api/users";
import { GoogleIcon, AppleIcon } from "@components/ui/icons/ProviderIcons";
import { useDataStore } from "@store/user_org";
import { TeamInfos } from "types";

function getRelativeTime(value: any): string {
  if (!value) return "";
  let date: Date;
  if (value?.seconds != null) {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string") {
    date = new Date(value);
  } else {
    return "";
  }
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 2) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} j`;
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const CardHeader = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex flex-row items-center gap-2 mb-2">
    <Icon1 name={icon} size={22} />
    <div className="text-h2  text-black">{title}</div>
  </div>
);

const AddressRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row items-center text-h2 text-neutral-400">
    <div className="w-100">{label}</div>
    <div className="flex-1 ">{value}</div>
  </div>
);

export const InfosSection = ({
  project,
  setProject,
  isGuest = false,
}: {
  project: Project;
  setProject?: (project: Project) => void;
  isGuest?: boolean;
}) => {
  const [addressCopied, setAddressCopied] = useState(false);
  const [contactLastActive, setContactLastActive] = useState<
    Record<string, string>
  >({});
  const [contactProviders, setContactProviders] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    const contactsWithUser = project.infos.client.contacts.filter(
      (c) => c.user_id,
    );
    if (contactsWithUser.length === 0) return;
    contactsWithUser.forEach(async (contact) => {
      try {
        const user = await usersApi.get(contact.user_id!);
        const raw = user.activity?.last_active_time;
        const relative = getRelativeTime(raw);
        if (relative) {
          setContactLastActive((prev) => ({
            ...prev,
            [contact.user_id!]: relative,
          }));
        }
        const providers = (user.infos.third_party_providers || []).map(
          (tp) => tp.provider,
        );
        if (providers.length > 0) {
          setContactProviders((prev) => ({
            ...prev,
            [contact.user_id!]: providers,
          }));
        }
      } catch {
        // ignore
      }
    });
  }, [project.infos.client.contacts]);

  // Card 1: Informations dossier
  const homeFields = [
    { label: "Adresse", value: project.infos.general.home_address || "" },
    { label: "Code Postal", value: project.infos.general.home_postal || "" },
    { label: "Ville", value: project.infos.general.home_city || "" },
  ].filter((o) => o.value);

  const projectFields = [
    { label: "Adresse", value: project.infos.general.address || "" },
    { label: "Code Postal", value: project.infos.general.postal || "" },
    { label: "Ville", value: project.infos.general.city || "" },
  ].filter((o) => o.value);

  const cadastreFields = [
    { label: "Lot", value: project.infos.general.lot || "" },
    { label: "Section", value: project.infos.general.section || "" },
    { label: "N° cadastre", value: project.infos.general.cadastre_number || "" },
  ].filter((o) => o.value);

  const fullAddress = projectFields.map((f) => f.value).join(", ");

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress);
    showNotification({
      type: "success",
      message: "Adresse copiée dans le presse-papier !",
    });
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 300);
  };

  // Card 2: Informations client
  const dateOptions = [
    {
      label: "Création",
      value: project.infos.general.creation_date
        ? getLocalFromFlutter(project.infos.general.creation_date)
        : "",
    },
    {
      label: "Activité",
      value: project.infos.general.last_updated
        ? getLocalFromFlutter(project.infos.general.last_updated)
        : "",
    },
  ];

  return (
    <div className="flex flex-col w-full lg:w-400 gap-2.5 lg:flex-shrink-0">
      {/* Card 1 - Informations dossier */}
      <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
        <CardHeader icon="folder" title="Informations dossier" />
        {homeFields.length > 0 && (
          <>
            <div className="text-h3 font-medium text-neutral-500 mb-1">Domicile</div>
            <div className="flex flex-col mb-2">
              {homeFields.map((field, index) => (
                <AddressRow key={index} label={field.label} value={field.value} />
              ))}
            </div>
          </>
        )}
        {projectFields.length > 0 && (
          <>
            <div className="text-h3 font-medium text-neutral-500 mb-1">Adresse du projet</div>
            <div className="flex flex-row items-center">
              <div className="flex flex-col flex-1">
                {projectFields.map((field, index) => (
                  <AddressRow key={index} label={field.label} value={field.value} />
                ))}
                {cadastreFields.map((field, index) => (
                  <AddressRow key={`cad-${index}`} label={field.label} value={field.value} />
                ))}
              </div>
              <div
                onClick={handleCopyAddress}
                className="flex items-center justify-center w-8 cursor-pointer self-center"
              >
                <Icon1
                  animated={true}
                  name="copy"
                  size={addressCopied ? 24 : 20}
                  color={addressCopied ? colors.green_400 : colors.neutral_300}
                />
              </div>
            </div>
          </>
        )}
        {!isGuest && project.infos.general.notes && (
          <>
            <div className="border-t border-neutral-200 my-3" />
            <div className="text-h3 text-neutral-400">
              {project.infos.general.notes}
            </div>
            <div className="text-h5 text-neutral-300 italic mt-1">
              visible uniquement en interne
            </div>
          </>
        )}
      </div>

      {/* Card 2 - Informations client */}
      <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
        <CardHeader icon="client" title="Informations client" />
        <CopiableInfosSection options={dateOptions} width="w-70" />
        {project.infos.client.contacts.map((contact, index) => {
          const contactOptions: { label: string; value: string }[] = [];
          if (contact.email) {
            contactOptions.push({ label: "Email", value: contact.email });
          }
          if (contact.phone_number) {
            contactOptions.push({ label: "Tel", value: contact.phone_number });
          }
          const providers = contact.user_id
            ? contactProviders[contact.user_id] || []
            : [];
          return (
            <div
              key={index}
              className="flex flex-col gap-1 mt-2 p-3 border border-neutral-200 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="text-h3 font-medium text-neutral-400">
                    {contact.prefix === "SCI" && contact.sci_name
                      ? `SCI ${contact.sci_name} - ${contact.first_name} ${contact.last_name}`
                      : `${contact.prefix} ${contact.first_name} ${contact.last_name}`}
                  </div>
                  {providers.includes("google") && <GoogleIcon size={14} />}
                  {providers.includes("apple") && <AppleIcon size={14} />}
                </div>
                {contact.user_id && contactLastActive[contact.user_id] && (
                  <div className="text-h5 text-neutral-400 flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    {contactLastActive[contact.user_id]}
                  </div>
                )}
              </div>
              <CopiableInfosSection options={contactOptions} width="w-70" />
            </div>
          );
        })}
      </div>

      {/* Card 3 - Membres de l'équipe */}
      {!isGuest ? (
        <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
          <CardHeader icon="users" title="Membres de l'équipe" />
          <MemberAssignmentBlock
            team={project.infos.team}
            onTeamChange={(team) => {
              if (setProject) {
                setProject({
                  ...project,
                  infos: { ...project.infos, team },
                });
              }
            }}
          />
        </div>
      ) : (
        <GuestTeamContactCard team={project.infos.team} />
      )}
    </div>
  );
};

// Whether a given member shows up in the client view is gated by the
// project's `visible_member_ids` list — this array is just the order/labels.
// All team roles, dessinateur included, can be opted-in via the eye toggle.
const guestVisibleRoles: { key: keyof TeamInfos; label: string }[] = [
  { key: "commercial_id", label: "Commercial" },
  { key: "conductor_id", label: "Conducteur de travaux" },
  { key: "dessinateur_id", label: "Dessinateur" },
  { key: "sav_id", label: "SAV" },
  { key: "comptability_id", label: "Comptabilité" },
  { key: "communication_id", label: "Communication" },
];

const GuestTeamContactCard = ({ team }: { team?: TeamInfos }) => {
  const orgMembers = useDataStore((state) => state.orgMembers);

  const visibleIds = new Set(team?.visible_member_ids || []);
  const assignedMembers = guestVisibleRoles
    .map((role) => {
      const userId = team?.[role.key] as string | undefined;
      if (!userId) return null;
      if (!visibleIds.has(userId)) return null;
      const member = orgMembers.find((m) => m.id === userId);
      if (!member) return null;
      // Inactive employees (set inactif by an admin) are never shown in
      // the client-facing contact card even if they're still listed in
      // visible_member_ids from before their departure.
      if (member.status === "inactive") return null;
      return { role: role.label, member };
    })
    .filter(Boolean) as { role: string; member: typeof orgMembers[0] }[];

  if (assignedMembers.length === 0) return null;

  return (
    <div className="flex flex-col bg-white rounded-xl p-4 shadow-lg border">
      <CardHeader icon="users" title="Vos interlocuteurs" />
      <div className="flex flex-col gap-2">
        {assignedMembers.map(({ role, member }) => (
          <div
            key={member.id}
            className="flex flex-col gap-0.5 p-3 border border-neutral-200 rounded-lg"
          >
            <div className="text-h3 font-medium text-black">
              {member.infos?.display_name || member.infos?.full_name || ""}
            </div>
            <div className="text-h4 text-neutral-400">{role}</div>
            {member.infos?.email && (
              <div className="flex items-center gap-1 mt-1">
                <Icon1 name="email" size={14} color="grey" />
                <a
                  href={`mailto:${member.infos.email}`}
                  className="text-h4 text-blue-500 hover:underline"
                >
                  {member.infos.email}
                </a>
              </div>
            )}
            {member.infos?.phone_number && (
              <div className="flex items-center gap-1">
                <Icon1 name="phone" size={14} color="grey" />
                <a
                  href={`tel:${member.infos.phone_number}`}
                  className="text-h4 text-blue-500 hover:underline"
                >
                  {member.infos.phone_number}
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
